import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { MAPAPI } from "../axiosInstance";

const GOOGLE_MAPS_SCRIPT_ID = "google-maps-places-script";
let googleMapsPromise = null;

const loadGoogleMaps = () => {
  if (!MAPAPI) {
    return Promise.reject(new Error("Google Maps API key is missing."));
  }

  if (googleMapsPromise) {
    return googleMapsPromise;
  }

  googleMapsPromise = new Promise((resolve, reject) => {
    if (window.google?.maps?.places) {
      resolve(window.google);
      return;
    }

    const existing = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
    if (existing) {
      if (existing.dataset.loaded === "true" && !window.google?.maps?.places) {
        existing.remove();
      } else {
        existing.addEventListener("load", () => {
          if (window.google?.maps?.places) {
            resolve(window.google);
          } else {
            googleMapsPromise = null;
            reject(new Error("Google Places library was not loaded."));
          }
        });
        existing.addEventListener("error", (error) => {
          googleMapsPromise = null;
          reject(error);
        });
        return;
      }
    }

    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    // Removed callback=initMap - we resolve via script.onload instead.
    // Keeping an unused global callback param throws "initMap is not defined"
    // in the console on every page load.
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(MAPAPI)}&libraries=places&v=weekly`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      script.dataset.loaded = "true";
      if (window.google?.maps?.places) {
        resolve(window.google);
      } else {
        googleMapsPromise = null;
        reject(new Error("Google Places library was not loaded."));
      }
    };
    script.onerror = (error) => {
      googleMapsPromise = null;
      reject(error);
    };
    document.body.appendChild(script);
  });

  return googleMapsPromise;
};

const LocationPickerStyles = () => (
  <style>{`
    .pac-container {
      z-index: 10000 !important;
    }
  `}</style>
);

const getAddressValue = (components, types) => {
  const typeList = Array.isArray(types) ? types : [types];
  for (const type of typeList) {
    const match = components.find((component) => component.types.includes(type));
    if (match?.long_name) return match.long_name;
  }
  return "";
};

const getAddressShortValue = (components, types) => {
  const typeList = Array.isArray(types) ? types : [types];
  for (const type of typeList) {
    const match = components.find((component) => component.types.includes(type));
    if (match?.short_name) return match.short_name;
  }
  return "";
};

const compactParts = (parts) => parts.map((part) => String(part || "").trim()).filter(Boolean);

const buildFullAddress = (place, components) => {
  const streetNumber = getAddressValue(components, "street_number");
  const route = getAddressValue(components, "route");
  const premise = getAddressValue(components, "premise");
  const subpremise = getAddressValue(components, "subpremise");
  const neighborhood = getAddressValue(components, "neighborhood");
  const sublocality = getAddressValue(components, [
    "sublocality_level_1",
    "sublocality",
  ]);
  const city = getAddressValue(components, [
    "locality",
    "postal_town",
    "administrative_area_level_2",
    "administrative_area_level_3",
  ]);
  const statename = getAddressValue(components, "administrative_area_level_1");
  const country = getAddressValue(components, "country");
  const pincode = getAddressValue(components, ["postal_code", "postal_code_prefix"]);

  const firstLine = compactParts([
    compactParts([subpremise, premise]).join(", "),
    compactParts([streetNumber, route]).join(" "),
  ]).join(", ");

  const componentAddress = compactParts([
    firstLine,
    neighborhood,
    sublocality,
    city,
    statename,
    pincode,
    country,
  ]).join(", ");

  return componentAddress || place.formatted_address || place.name || "";
};

const buildLocationPayload = (place, zones = []) => {
  const components = place.address_components || [];

  // Broader fallback chains - many places (small towns, city-states, etc.)
  // don't have every admin level Google normally returns.
  const statename = getAddressValue(components, [
    "administrative_area_level_1",
    "administrative_area_level_2",
  ]);
  const city = getAddressValue(components, [
    "locality",
    "postal_town",
    "administrative_area_level_2",
    "administrative_area_level_3",
    "sublocality_level_1",
    "sublocality",
  ]);
  const country = getAddressValue(components, "country");
  const pincode = getAddressValue(components, ["postal_code", "postal_code_prefix"]);
  const address = buildFullAddress(place, components);
  const latitude = place.geometry?.location?.lat?.();
  const longitude = place.geometry?.location?.lng?.();

  const statenameShort = getAddressShortValue(components, [
    "administrative_area_level_1",
    "administrative_area_level_2",
  ]);
  const matchedState = zones.find(
    (zone) =>
      String(zone.name || "").toLowerCase() === String(statename || "").toLowerCase() ||
      String(zone.name || "").toLowerCase() === String(statenameShort || "").toLowerCase()
  );

  return {
    address,
    city,
    country,
    pincode,
    statename,
    state: matchedState?._id || "",
    latitude: latitude === undefined ? "" : String(latitude),
    longitude: longitude === undefined ? "" : String(longitude),
  };
};

// Only lat/lng + address are truly essential. Everything else (pincode,
// city, state) can legitimately be missing for some places - we should
// still hand back whatever we got instead of discarding the whole thing.
const hasCoreLocationParts = (payload) =>
  payload.address && payload.latitude && payload.longitude;

const missingFieldLabels = (payload) => {
  const labels = [];
  if (!payload.city) labels.push("city");
  if (!payload.statename) labels.push("state");
  if (!payload.pincode) labels.push("pincode");
  if (!payload.country) labels.push("country");
  return labels;
};

const mergeLocationPayload = (primary, fallback) => ({
  address: primary.address || fallback.address || "",
  city: primary.city || fallback.city || "",
  country: primary.country || fallback.country || "",
  pincode: primary.pincode || fallback.pincode || "",
  statename: primary.statename || fallback.statename || "",
  state: primary.state || fallback.state || "",
  latitude: primary.latitude || fallback.latitude || "",
  longitude: primary.longitude || fallback.longitude || "",
});

const scoreLocationPayload = (payload) =>
  [
    payload.address,
    payload.city,
    payload.country,
    payload.pincode,
    payload.statename,
    payload.state,
    payload.latitude,
    payload.longitude,
  ].filter(Boolean).length;

const mergeBestGeocodeResults = (results = [], zones = []) => {
  const payloads = results.map((result) => buildLocationPayload(result, zones));
  const bestPayload = payloads.reduce(
    (best, payload) =>
      scoreLocationPayload(payload) > scoreLocationPayload(best) ? payload : best,
    {}
  );

  return payloads.reduce(
    (merged, payload) => mergeLocationPayload(merged, payload),
    bestPayload
  );
};

const geocodePlaceId = (google, placeId, zones) =>
  new Promise((resolve) => {
    if (!placeId) {
      resolve({});
      return;
    }

    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ placeId }, (results, status) => {
      if (status !== "OK" || !results?.length) {
        resolve({});
        return;
      }

      resolve(mergeBestGeocodeResults(results, zones));
    });
  });

const reverseGeocodeLocation = (google, lat, lng, zones) =>
  new Promise((resolve) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status !== "OK" || !results?.length) {
        resolve({});
        return;
      }
      resolve(mergeBestGeocodeResults(results, zones));
    });
  });

const resolvePlacePayload = async (google, place, zones) => {
  const primaryPayload = buildLocationPayload(place, zones);
  const lat = place.geometry?.location?.lat?.();
  const lng = place.geometry?.location?.lng?.();

  // Always try reverse geocode as a fallback fill-in when something is
  // missing, as long as we have coordinates to work with.
  const isComplete =
    primaryPayload.city &&
    primaryPayload.statename &&
    primaryPayload.pincode &&
    primaryPayload.country;

  if (isComplete || lat === undefined || lng === undefined) {
    return primaryPayload;
  }

  const placeIdPayload = await geocodePlaceId(google, place.place_id, zones);
  const withPlaceId = mergeLocationPayload(primaryPayload, placeIdPayload);

  const placeIdComplete =
    withPlaceId.city &&
    withPlaceId.statename &&
    withPlaceId.pincode &&
    withPlaceId.country;

  if (placeIdComplete) {
    return withPlaceId;
  }

  const fallbackPayload = await reverseGeocodeLocation(google, lat, lng, zones);
  return mergeLocationPayload(withPlaceId, fallbackPayload);
};

const LocationPicker = ({
  value = "",
  zones = [],
  onSelect,
  label = "Search map address",
  compact = false,
  helperText = "Choose a map result to auto-fill address, city, state, country, pincode, latitude and longitude.",
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [search, setSearch] = useState(value || "");
  const [loadingCurrent, setLoadingCurrent] = useState(false);

  useEffect(() => {
    setSearch(value || "");
  }, [value]);

  useEffect(() => {
    let mounted = true;

    loadGoogleMaps()
      .then((google) => {
        if (!mounted || !inputRef.current || autocompleteRef.current) return;

        autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
          fields: ["address_components", "formatted_address", "geometry", "name", "place_id", "types"],
        });

        autocompleteRef.current.addListener("place_changed", async () => {
          const place = autocompleteRef.current.getPlace();

          // Happens when the user types free text and hits Enter without
          // actually clicking a suggestion from the dropdown - Google
          // returns a place with no geometry/address_components.
          if (!place?.geometry) {
            toast.error("Please select a location from the suggestion list.");
            return;
          }

          const payload = await resolvePlacePayload(google, place, zones);

          if (!hasCoreLocationParts(payload)) {
            toast.error("Could not read this location's address. Please try another.");
            return;
          }

          const missing = missingFieldLabels(payload);
          if (missing.length) {
            toast(`Some details (${missing.join(", ")}) were not found for this location. Please check before saving.`, { icon: "⚠️" });
          }

          setSearch(payload.address);
          onSelect?.(payload);
        });
      })
      .catch(() => {
        toast.error("Map search could not be loaded.");
      });

    return () => {
      mounted = false;
    };
  }, [onSelect, zones]);

  const chooseCurrentLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Current location is not supported in this browser.");
      return;
    }

    setLoadingCurrent(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const google = await loadGoogleMaps();
          const payload = await reverseGeocodeLocation(google, coords.latitude, coords.longitude, zones);
          setLoadingCurrent(false);

          if (!hasCoreLocationParts(payload)) {
            toast.error("Could not read your current address. Please choose another location.");
            return;
          }

          const missing = missingFieldLabels(payload);
          if (missing.length) {
            toast(`Some details (${missing.join(", ")}) were not found for this location. Please check before saving.`, { icon: "⚠️" });
          }

          setSearch(payload.address);
          onSelect?.(payload);
        } catch {
          setLoadingCurrent(false);
          toast.error("Unable to read your current address. Please choose another location.");
        }
      },
      () => {
        setLoadingCurrent(false);
        toast.error("Location permission denied. Please search and choose a location.");
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  return (
    <>
      <LocationPickerStyles />
      <div className="mb-3">
        <label className="form-label text-dark">{label}</label>
        <div className={compact ? "d-flex flex-column gap-2" : "input-group"}>
          <input
            ref={inputRef}
            className={compact ? "form-control w-100" : "form-control"}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search location on map"
          />
          <button
            className={`btn btn-outline-primary ${compact ? "w-100" : ""}`}
            type="button"
            onClick={chooseCurrentLocation}
            disabled={loadingCurrent}
          >
            {loadingCurrent ? "Locating..." : "Current location"}
          </button>
        </div>
        {helperText ? <small className="text-muted">{helperText}</small> : null}
      </div>
    </>
  );
};

export default LocationPicker;
